import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService } from '../../../features/auth/services/auth.service';
import { StorageService } from '../../../core/services/storage.service';
import { LOCAL_STORAGE_KEYS } from '../../../core/constants/local-storage-keys';
import { TenantSelectionService } from '../../../core/services/tenant-selection.service';
import { Tenant } from '../../../features/tenant/services/tenant.service';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type EmbeddedTenant = Pick<Tenant, 'id' | 'name'> &
  Partial<Pick<Tenant, 'displayName' | 'sortOrder' | 'logoImage' | 'branding'>>;

type ParentInitMessage = {
  type: 'AUTHHUB_INIT';
  token: string;
  tenant?: EmbeddedTenant;
};

type ParentLogoutMessage = {
  type: 'AUTHHUB_LOGOUT';
};

@Component({
  selector: 'app-embedded-bootstrap',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './embedded-bootstrap.html',
  styleUrls: ['./embedded-bootstrap.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmbeddedBootstrapComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private storageService = inject(StorageService);
  private tenantSelectionService = inject(TenantSelectionService);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();

  isReady = false;
  errorText = '';
  // private readonly allowedParentOrigins = new Set<string>([
  //   'https://erp.dev.inovepic.dev',
  //   // 'https://erp.inovepic.dev',
  // ]);

  private readonly allowedParentOrigins = new Set<string>([
    'http://localhost:4201', // local Warehouse
    'https://erp.dev.inovepic.dev',
    // 'https://erp.inovepic.dev',
  ]);

  private isParentMessage(data: unknown): data is ParentInitMessage | ParentLogoutMessage {
    return !!data && typeof data === 'object' && 'type' in data;
  }

  private isEmbeddedTenant(value: unknown): value is EmbeddedTenant {
    return (
      !!value &&
      typeof value === 'object' &&
      'id' in value &&
      typeof value.id === 'string' &&
      'name' in value &&
      typeof value.name === 'string'
    );
  }

  private toTenant(tenant: EmbeddedTenant): Tenant {
    return {
      id: tenant.id,
      name: tenant.name,
      displayName: tenant.displayName ?? tenant.name,
      sortOrder: tenant.sortOrder ?? 0,
      isDeactivated: false,
      logoImage: tenant.logoImage ?? '',
      branding: tenant.branding ?? {
        logoImage: '',
        branding: '',
      },
    };
  }

  ngOnInit(): void {
    if (this.authService.isAuthenticated()) {
      this.isReady = true;
      this.cdr.markForCheck();
      return;
    }

    window.addEventListener('message', this.onMessage);

    this.postToParent({ type: 'AUTHHUB_READY' });

    timer(8000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.authService.isAuthenticated()) {
          this.errorText =
            'AuthHub is opened inside Warehouse but no session token was received. Please re-login in Warehouse.';
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onMessage = (event: MessageEvent): void => {
    if (!this.allowedParentOrigins.has(event.origin)) return;

    const data = event.data;
    if (!this.isParentMessage(data)) return;

    if (data.type === 'AUTHHUB_INIT') {
      const { token, tenant } = data;

      if (token && typeof token === 'string') {
        this.authService.saveAuthInfo(token);

        if (this.isEmbeddedTenant(tenant)) {
          const normalizedTenant = this.toTenant(tenant);
          this.storageService.setItem(LOCAL_STORAGE_KEYS.TENANT, normalizedTenant, 'localStorage');
          this.tenantSelectionService.setSelectedTenant(normalizedTenant);
        }

        this.isReady = true;
        this.errorText = '';
        this.cdr.markForCheck();
      }
      return;
    }

    if (data.type === 'AUTHHUB_LOGOUT') {
      this.authService.logout();
      this.isReady = false;
      this.errorText = 'You have been logged out.';
      this.cdr.markForCheck();
    }
  };

  private postToParent(payload: unknown): void {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, '*');
    }
  }
}
